'use client';
import React from 'react';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort, fmtPct } from '@/lib/financial-model';
import {
  TrendingUp,
  DollarSign,
  BarChart2,
  Clock,
  Percent,
  AlertTriangle,
  Zap,
} from 'lucide-react';

function KPICard({
  label,
  value,
  subValue,
  icon,
  variant,
  span2,
  note,
}: {
  label: string;
  value: string;
  subValue?: string;
  icon: React.ReactNode;
  variant: 'default' | 'gold' | 'warning' | 'danger' | 'info';
  span2?: boolean;
  note?: string;
}) {
  const styles = {
    gold: {
      bg: 'rgba(201,168,76,0.06)',
      border: 'rgba(201,168,76,0.2)',
      iconBg: 'rgba(201,168,76,0.12)',
      iconColor: 'var(--gold)',
      valueColor: 'var(--gold)',
    },
    warning: {
      bg: 'rgba(232,160,32,0.05)',
      border: 'rgba(232,160,32,0.2)',
      iconBg: 'rgba(232,160,32,0.1)',
      iconColor: 'var(--warning)',
      valueColor: 'var(--warning)',
    },
    danger: {
      bg: 'rgba(224,82,82,0.05)',
      border: 'rgba(224,82,82,0.2)',
      iconBg: 'rgba(224,82,82,0.1)',
      iconColor: 'var(--downside)',
      valueColor: 'var(--downside)',
    },
    info: {
      bg: 'rgba(74,158,255,0.05)',
      border: 'rgba(74,158,255,0.2)',
      iconBg: 'rgba(74,158,255,0.1)',
      iconColor: 'var(--accent)',
      valueColor: 'var(--accent)',
    },
    default: {
      bg: 'var(--card)',
      border: 'var(--border)',
      iconBg: 'var(--surface-2)',
      iconColor: 'var(--text-tertiary)',
      valueColor: 'var(--text-primary)',
    },
  };

  const s = styles[variant];

  return (
    <div
      className={`kpi-card ${span2 ? 'col-span-2' : ''}`}
      style={{ background: s.bg, borderColor: s.border }}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
          style={{ background: s.iconBg, color: s.iconColor }}
        >
          {icon}
        </div>
        {span2 && (
          <div
            className="px-2 py-0.5 rounded-sm"
            style={{
              background: 'rgba(201,168,76,0.1)',
              border: '1px solid rgba(201,168,76,0.2)',
              fontSize: '0.55rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              color: 'var(--gold)',
              textTransform: 'uppercase',
            }}
          >
            PRIMARY KPI
          </div>
        )}
      </div>
      <p className="kpi-label mb-1.5">{label}</p>
      <p className="kpi-value-lg font-mono-nums" style={{ color: s.valueColor }}>
        {value}
      </p>
      {subValue && (
        <p
          className="font-mono-nums mt-1"
          style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)' }}
        >
          {subValue}
        </p>
      )}
      {note && (
        <p
          className="mt-2 leading-relaxed"
          style={{
            fontSize: '0.6rem',
            color: 'var(--text-tertiary)',
            borderTop: '1px solid var(--divider)',
            paddingTop: '0.5rem',
          }}
        >
          {note}
        </p>
      )}
    </div>
  );
}

export default function KPIBentoGrid() {
  const { model } = useModel();
  const yr1 = model.years[1];
  const yr2 = model.years[2];

  const projectIrrPct = model.projectIRR * 100;
  const equityIrrPct = model.equityIRR * 100;
  const yr2Dscr = yr2 ? yr2.dscr : 0;
  const dscrVariant = yr2Dscr >= 2 ? 'gold' : yr2Dscr >= 1.5 ? 'warning' : 'danger';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {/* Hero: Project IRR */}
      <KPICard
        label="Project IRR (10-Year)"
        value={`${projectIrrPct.toFixed(1)}%`}
        subValue={`Equity IRR: ${equityIrrPct.toFixed(1)}%`}
        icon={<TrendingUp size={16} />}
        variant="gold"
        span2
        note="Unlevered project return on total capital. Target > 14% WACC."
      />
      {/* NPV */}
      <KPICard
        label="Project NPV @ 14%"
        value={fmtCrShort(model.projectNPV)}
        subValue="Discount Rate 14%"
        icon={<DollarSign size={16} />}
        variant="info"
      />
      {/* Payback */}
      <KPICard
        label="Simple Payback"
        value={
          model.paybackYearFractional > 0 ? `Yr ${model.paybackYearFractional.toFixed(1)}` : 'N/A'
        }
        subValue="Project FCF basis"
        icon={<Clock size={16} />}
        variant="default"
      />
      {/* Year 1 Revenue */}
      <KPICard
        label="Year 1 Revenue"
        value={yr1 ? fmtCrShort(yr1.revenue) : '—'}
        subValue={
          yr1
            ? `${(yr1.utilisation * 100).toFixed(0)}% util · ${yr1.production.toLocaleString('en-IN')} T`
            : ''
        }
        icon={<BarChart2 size={16} />}
        variant="default"
      />
      {/* Year 2 Revenue */}
      <KPICard
        label="Year 2 Revenue"
        value={yr2 ? fmtCrShort(yr2.revenue) : '—'}
        subValue={yr2 ? `${(yr2.utilisation * 100).toFixed(0)}% util · steady state` : ''}
        icon={<Zap size={16} />}
        variant="default"
      />
      {/* Year 2 PAT */}
      <KPICard
        label="Year 2 PAT"
        value={yr2 ? fmtCrShort(yr2.pat) : '—'}
        subValue={yr2 ? `Margin: ${fmtPct(yr2.patMarginPct)}` : ''}
        icon={<Percent size={16} />}
        variant="gold"
      />
      {/* DSCR */}
      <KPICard
        label="DSCR (Year 2)"
        value={yr2Dscr > 0 ? `${yr2Dscr.toFixed(2)}x` : 'N/A'}
        subValue="Target > 1.5x (CGTMSE)"
        icon={<AlertTriangle size={16} />}
        variant={dscrVariant}
        note={yr2Dscr < 1.5 ? '⚠ Below lender covenant — review assumptions' : undefined}
      />
    </div>
  );
}
